import { IntlShape } from 'react-intl';
import { Column } from 'material-table';
import { TimeRange } from 'redux/ducks/reportFilter';
import { BenchmarkReport } from 'redux/ducks/reportFilter/selectors';
import { SheetFormat } from 'pages/Report/Advanced/utils/tableExport';
import { AdvancedReportType, BenchmarkReportType } from 'pages/Report/Advanced';
import { unformat } from 'utils/number-format';

type ExportNameMap = Record<BenchmarkReportType, string>;

const filenames: ExportNameMap = {
  benchGuests: `guest_registrations`,
  benchWaste: `foodwaste`,
  benchWastePerGuest: `foodwaste_per_guest`
};

const sheetnames: ExportNameMap = {
  benchGuests: `guest registrations`,
  benchWaste: `foodwaste`,
  benchWastePerGuest: `foodwaste per guest`
};

export const createTableColumnConfig = (
  intl: IntlShape,
  { data }: { data: BenchmarkReport[] },
  isBenchByLabel: boolean
): Column<BenchmarkReport>[] => [
  {
    title: intl.formatMessage({ id: 'account' }),
    field: 'accountName',
    cellStyle: { borderColor: 'rgba(0, 0, 0, 0.05)' }
  },
  ...getColumnsFromData(data, intl, isBenchByLabel),
  {
    title: intl.formatMessage({ id: 'total' }),
    field: 'total',
    customSort: (row1: BenchmarkReport, row2: BenchmarkReport) =>
      unformat(row1.total) - unformat(row2.total),
    headerStyle: { fontWeight: 'bold' },
    cellStyle: { borderColor: 'rgba(0, 0, 0, 0.05)', fontWeight: 'bold' }
  }
];

export const createExportConfig = (
  intl: IntlShape,
  { timeRange, type }: { timeRange: TimeRange; type: AdvancedReportType }
) => ({
  fileName: `Benchmark_${filenames[type as BenchmarkReportType]}_${timeRange.from}_${timeRange.to}`,
  sheetName: `Benchmark ${sheetnames[type as BenchmarkReportType]}`,
  render: (headers: Column<BenchmarkReport>[], dataRow: BenchmarkReport): SheetFormat =>
    headers.reduce(
      (all, header) => {
        const data = dataRow[header.field];

        if (data === undefined) {
          return all;
        }

        return { ...all, [header.title as string]: data };
      },
      {} as { [title: string]: string | number }
    )
});

const getColumnsFromData = (data: BenchmarkReport[], intl: IntlShape, isBenchByLabel = false) => {
  const { accountName, total, tableData, ...rest } = data?.[0] || { tableData: {} };
  return Object.keys(rest).map((column) => ({
    title:
      isBenchByLabel && column === 'foodwaste'
        ? intl.formatMessage({ id: `label.system.${column}` })
        : column,
    field: column,
    customSort: (row1: BenchmarkReport, row2: BenchmarkReport) =>
      unformat(row1[column]) - unformat(row2[column]),
    cellStyle: { borderColor: 'rgba(0, 0, 0, 0.05)' }
  }));
};
